import { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { ZoomData } from '@mui/x-charts-pro/models';
import { skipToken } from '@reduxjs/toolkit/query';
import { APP_DATE_FORMAT } from '@/constants/dateFormats';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { useInScopeAccountIds } from '@/hooks/useInScopeAccountIds';
import { useLocalization } from '@/hooks/useLocalization';
import WealthTrendLineChart, {
  WEALTH_TREND_X_AXIS_ID,
} from '@/modules/advisor/wealthTrend/WealthTrendLineChart';
import {
  hasTrendPoints,
  snapZoomToDays,
  zoomWindow,
} from '@/modules/advisor/wealthTrend/wealthTrendChartData';
import {
  historicalComparisonQueryArg,
  isCustomGroupBy,
} from '@/components/grids/historical/historicalComparisonQueryArg';
import {
  useGetAccountHistoricalComparisonQuery,
  useGetAccountsHistoricalQuery,
  useGetAccountsQuery,
  type AccountsHistoricalQueryArg,
} from '@/store/accountsApi';
import { filterByInScopeIds } from '@/utils/historicalAccountScope';
import { formatAppDate, parseAppDate, toApiDateParam } from '@/utils/date';
import {
  comparisonEndIsCurrent,
  lookupLiveEndingBalance,
  withLiveEndingBalance,
} from '@/utils/historicalBalance';
import {
  buildHistoricalTrendChart,
  endingTrendSeriesName,
  withLiveEndpoint,
  type HistoricalTrendGroupConfig,
  type HistoricalTrendRecord,
} from './historicalTrendData';

const FULL_RANGE: readonly ZoomData[] = [
  { axisId: WEALTH_TREND_X_AXIS_ID, start: 0, end: 100 },
];

const TOUCH_POINTER = '(pointer: coarse)';

export interface HistoricalTrendSeriesRequest {
  trendType: string;
  groupConfig?: HistoricalTrendGroupConfig;
  label?: string;
  absolute?: boolean;
  renameSeries?: (name: string) => string;
}

interface HistoricalTrendChartProps {
  startDate?: string;
  endDate?: string;
  seriesRequests:
    | readonly [HistoricalTrendSeriesRequest]
    | readonly [HistoricalTrendSeriesRequest, HistoricalTrendSeriesRequest];
  excludedAccounts?: number[];
  excludedAccountTypes?: number[];
  excludedCategories?: number[];
  excludedInstitutions?: number[];
}

function useTrendSeries(
  request: HistoricalTrendSeriesRequest | undefined,
  baseArg: Omit<AccountsHistoricalQueryArg, 'trendType'> | null,
) {
  return useGetAccountsHistoricalQuery(
    request && baseArg
      ? {
          ...baseArg,
          trendType: request.trendType,
          ...(request.groupConfig ? { groupConfig: request.groupConfig } : {}),
        }
      : skipToken,
  );
}

function nonEmpty(values: number[] | undefined): number[] | undefined {
  return values && values.length > 0 ? values : undefined;
}

function tableGroupBy(
  requests: readonly HistoricalTrendSeriesRequest[],
): string {
  const customGrouping = requests.find(
    ({ groupConfig }) =>
      groupConfig && isCustomGroupBy(groupConfig.valueProperty),
  );
  return customGrouping?.groupConfig?.valueProperty ?? 'none';
}

function seriesName(
  request: HistoricalTrendSeriesRequest,
  name: string,
): string {
  return request.label ?? request.renameSeries?.(name) ?? name;
}

export default function HistoricalTrendChart({
  startDate,
  endDate,
  seriesRequests,
  excludedAccounts,
  excludedAccountTypes,
  excludedCategories,
  excludedInstitutions,
}: HistoricalTrendChartProps) {
  const { t } = useLocalization();
  const authLoaded = useAuthIsLoaded();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTouch = useMediaQuery(TOUCH_POINTER);

  const closedAccounts = useGetAccountsQuery(
    authLoaded && endDate ? { showClosed: true, asOfDate: endDate } : skipToken,
  );
  const closedAccountIds = useMemo(
    () =>
      (closedAccounts.currentData?.Records ?? [])
        .filter((account) => Boolean(account.ClosedDate))
        .map((account) => account.AccountId),
    [closedAccounts.currentData],
  );

  const endIsCurrent = comparisonEndIsCurrent(endDate);
  const {
    accountIds: inScopeAccountIds,
    balancesByAccountId,
    isLoading: areLiveBalancesLoading,
  } = useInScopeAccountIds({
    endDate: endIsCurrent ? endDate : undefined,
    excludedAccounts,
    excludedAccountTypes,
    excludedCategories,
    excludedInstitutions,
  });
  const tableRows = useGetAccountHistoricalComparisonQuery(
    authLoaded && endIsCurrent && startDate && endDate
      ? historicalComparisonQueryArg({
          startDate,
          endDate,
          groupBy: tableGroupBy(seriesRequests),
          excludedAccounts,
          excludedAccountTypes,
          excludedCategories,
          excludedInstitutions,
        })
      : skipToken,
  );
  const areTableRowsLoading =
    endIsCurrent &&
    !tableRows.isError &&
    (tableRows.isFetching || !tableRows.currentData);

  const baseArg = useMemo(() => {
    if (!authLoaded || !startDate || !endDate || !closedAccounts.currentData)
      return null;
    return {
      startDate: toApiDateParam(startDate),
      endDate: toApiDateParam(endDate),
      ExcludedAccounts: nonEmpty([
        ...new Set([...(excludedAccounts ?? []), ...closedAccountIds]),
      ]),
      ExcludedAccountTypes: nonEmpty(excludedAccountTypes),
      ExcludedCategories: nonEmpty(excludedCategories),
      ExcludedInstitutions: nonEmpty(excludedInstitutions),
    };
  }, [
    authLoaded,
    startDate,
    endDate,
    closedAccounts.currentData,
    closedAccountIds,
    excludedAccounts,
    excludedAccountTypes,
    excludedCategories,
    excludedInstitutions,
  ]);

  const first = useTrendSeries(seriesRequests[0], baseArg);
  const second = useTrendSeries(seriesRequests[1], baseArg);
  const results = seriesRequests[1] ? [first, second] : [first];

  const isError =
    closedAccounts.isError || results.some((result) => result.isError);
  const isLoading =
    !baseArg ||
    areLiveBalancesLoading ||
    areTableRowsLoading ||
    results.some((result) => result.isFetching || !result.currentData);

  const firstRecords = first.currentData?.Records;
  const secondRecords = second.currentData?.Records;
  const tableRecords = tableRows.isError
    ? undefined
    : tableRows.currentData?.Records;
  const chart = useMemo(() => {
    const recordsBySeries = [firstRecords, secondRecords];
    const inputs = seriesRequests.flatMap((request, index) => {
      const records: HistoricalTrendRecord[] = recordsBySeries[index] ?? [];
      return records.map((record) => ({
        name: seriesName(request, record.name),
        data: record.data,
        absolute: request.absolute,
      }));
    });
    const rawChart = buildHistoricalTrendChart(inputs);
    const endDay = parseAppDate(endDate)?.startOf('day');
    if (
      !endIsCurrent ||
      !endDay ||
      !tableRecords ||
      !balancesByAccountId ||
      balancesByAccountId.size === 0
    ) {
      return rawChart;
    }

    const openTableRecords = filterByInScopeIds(
      tableRecords,
      (record) => record.Id,
      inScopeAccountIds,
    ).filter((record) => !record.ClosedDate);
    const endingBalances = seriesRequests.flatMap((request) =>
      openTableRecords.flatMap((record) => {
        const name = endingTrendSeriesName(
          request.trendType,
          request.groupConfig,
          record,
        );
        const { DollarBalance } = withLiveEndingBalance(
          record,
          lookupLiveEndingBalance(record.Id, balancesByAccountId),
          endDate,
        );
        return name !== null &&
          DollarBalance !== null &&
          Number.isFinite(DollarBalance)
          ? [
              {
                name: seriesName(request, name),
                balance: DollarBalance,
                absolute: request.absolute,
              },
            ]
          : [];
      }),
    );
    return withLiveEndpoint(rawChart, endDay, endingBalances);
  }, [
    seriesRequests,
    firstRecords,
    secondRecords,
    tableRecords,
    endDate,
    endIsCurrent,
    inScopeAccountIds,
    balancesByAccountId,
  ]);

  const [zoomData, setZoomData] = useState<readonly ZoomData[]>(FULL_RANGE);
  const [zoomedFor, setZoomedFor] = useState(chart);
  if (zoomedFor !== chart) {
    setZoomedFor(chart);
    setZoomData(FULL_RANGE);
  }

  const handleZoomChange = useCallback(
    (next: ZoomData[]) => {
      setZoomData(
        next.map((entry) =>
          entry.axisId === WEALTH_TREND_X_AXIS_ID
            ? { ...entry, ...snapZoomToDays(chart.days, entry) }
            : entry,
        ),
      );
    },
    [chart.days],
  );

  const zoom = zoomData.find(
    (entry) => entry.axisId === WEALTH_TREND_X_AXIS_ID,
  );
  const zoomedDays = zoomWindow(chart.days, zoom ?? null);
  const noop = useCallback(() => undefined, []);

  if (isError) {
    return (
      <Alert
        severity='warning'
        data-testid='historical-trend-error'
      >
        {t('errorLoadingData', 'Error loading data.')}
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Skeleton
        variant='rounded'
        height={320}
        data-testid='historical-trend-loading'
      />
    );
  }

  if (!hasTrendPoints(chart)) {
    return (
      <Typography
        color='text.secondary'
        data-testid='historical-trend-empty'
      >
        {t('historicalTrend.noRecordsFound', 'No records found.')}
      </Typography>
    );
  }

  return (
    <Box data-testid='historical-trend-chart'>
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        flexWrap='wrap'
        gap={1}
        sx={{ mb: 1 }}
      >
        <Typography
          variant='body2'
          data-testid='historical-trend-visible-range'
        >
          {zoomedDays
            ? t('wealthTrend.fromTo', 'From {{start}} to {{end}}', {
                start: formatAppDate(zoomedDays.start, {
                  format: APP_DATE_FORMAT,
                }),
                end: formatAppDate(zoomedDays.end, { format: APP_DATE_FORMAT }),
              })
            : null}
        </Typography>
        <Stack
          direction='row'
          alignItems='center'
          gap={1}
        >
          <Typography
            variant='caption'
            color='text.secondary'
          >
            {isTouch
              ? t('wealthTrend.zoomHintTouch', 'Pinch the chart to zoom in')
              : t(
                  'wealthTrend.zoomHint',
                  'Click and drag in the chart to zoom in',
                )}
          </Typography>
          {zoomedDays ? (
            <Button
              size='small'
              variant='outlined'
              onClick={() => {
                setZoomData(FULL_RANGE);
              }}
            >
              {t('historicalTrend.reset', 'Reset')}
            </Button>
          ) : null}
        </Stack>
      </Stack>
      <WealthTrendLineChart
        key={JSON.stringify([
          isMobile,
          ...chart.lines.map((line) => line.name),
        ])}
        chart={chart}
        zoomData={zoomData}
        onZoomChange={handleZoomChange}
        showPopover={false}
        onOpenPoint={noop}
        isCompact={isMobile}
        legendHint={t('wealthTrend.legendHint', 'Click item to hide')}
        isTouch={isTouch}
        onDragStart={noop}
        hideLegend={isMobile || chart.lines.length < 2}
        showZoomSlider={!isMobile}
        tooltipDateFormat={APP_DATE_FORMAT}
      />
    </Box>
  );
}
